import { Injectable, Inject, signal, computed } from '@angular/core';
import {AppNotification, PUSH_PREVIEW_CONFIG, PushPreviewConfig} from "./notification-preview.config";

@Injectable({ providedIn: 'root' })
export class PushPreviewStore {
    // --- Internal State ---
    private readonly _notifications = signal<AppNotification[]>([]);
    private readonly _trayOpen = signal<boolean>(false);

    // New: Mute State
    private readonly _isMuted = signal<boolean>(false);
    private muteTimer: any = null;

    // --- Public Readonly API ---
    readonly notifications = this._notifications.asReadonly();
    readonly trayOpen = this._trayOpen.asReadonly();
    readonly totalCount = computed(() => this._notifications().length);

    constructor(@Inject(PUSH_PREVIEW_CONFIG) private config: PushPreviewConfig) {}

    ngOnDestroy() {
        this.clearMuteTimer();
    }

    /**
     * Ingest a new notification.
     */
    pushIncoming(notification: AppNotification): void {
        // 1. Check if Tray is Open
        if (this._trayOpen()) return;

        // 2. Check if Muted (Cooldown active)
        if (this._isMuted()) {
            // console.log('Push suppressed due to Dismiss All cooldown.');
            return;
        }

        this._notifications.update(list => [...list, notification]);
    }

    dismiss(id: string): void {
        this._notifications.update(list => list.filter(n => n.id !== id));
    }

    /**
     * Clear all AND start the mute cooldown.
     */
    dismissAll(): void {
        this._notifications.set([]);

        // Start the cooldown
        this.activateMuteCooldown();
    }

    setTrayOpen(open: boolean): void {
        this._trayOpen.set(open);
        if (open) {
            // If we open the tray, we clear previews, but we don't necessarily need to mute future ones
            // (unless you want that behavior). For now, just clear.
            this._notifications.set([]);
        }
    }

    reset(): void {
        this._notifications.set([]);
        this._trayOpen.set(false);
        this._isMuted.set(false);
        this.clearMuteTimer();
    }

    // --- Private Helpers ---

    private activateMuteCooldown() {
        this.clearMuteTimer();
        this._isMuted.set(true);

        // console.log(`Previews muted for ${this.config.muteDurationAfterDismissAllMs}ms`);

        this.muteTimer = setTimeout(() => {
            this._isMuted.set(false);
            // console.log('Previews un-muted.');
        }, this.config.muteDurationAfterDismissAllMs);
    }

    private clearMuteTimer() {
        if (this.muteTimer) {
            clearTimeout(this.muteTimer);
            this.muteTimer = null;
        }
    }
}